import { Fragment } from 'react'

import { Divider } from '#app/components/ui/divider.tsx'
import { ContactAdd } from '#app/routes/mocra+/_form+/components/contacts/add.tsx'
import { ContactCard } from '#app/routes/mocra+/_form+/components/contacts/card.tsx'
import {
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type PrimaryContactAssignments } from '#app/routes/mocra+/_form+/primary-contacts.tsx'
import { type SectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

type AssignedContact = {
	readonly id: string
	readonly name: string
	readonly title: string
	readonly email: string
	readonly phone: string
}

export type ContactAssignmentSectionConfig = {
	readonly key: keyof PrimaryContactAssignments
	readonly title: string
	readonly description: string
	readonly addLabel: string
	readonly assignedId: string
	readonly assignedContact: AssignedContact | null
	readonly resources: SectionResources
}

type Props = {
	readonly sections: ReadonlyArray<ContactAssignmentSectionConfig>
	readonly contactCandidates: readonly ContactCandidate[]
	readonly onAssign: (
		role: keyof PrimaryContactAssignments,
		draft: ContactDraft,
	) => void
	readonly onEdit: (
		role: keyof PrimaryContactAssignments,
		contactId: string,
	) => void
	readonly onClear: (role: keyof PrimaryContactAssignments) => void
}

export function ContactAssignmentsSection({
	sections,
	contactCandidates,
	onAssign,
	onEdit,
	onClear,
}: Props) {
	return (
		<>
			{sections.map((section, index) => (
				<Fragment key={`${section.key}`}>
					<Section
						title={section.title}
						description={section.description}
						childrenClassName="space-y-4"
						mocraSections={section.resources.mocraSections}
						templates={section.resources.templates}
					>
						{section.assignedContact ? (
							(() => {
								const contactId = section.assignedContact.id
								return (
									<ContactCard
										name={section.assignedContact.name}
										title={section.assignedContact.title}
										email={section.assignedContact.email}
										phone={section.assignedContact.phone}
										contextLabel={section.title}
										onClick={() => onEdit(section.key, contactId)}
										onRemove={() => onClear(section.key)}
										removeLabel="Clear role"
										removeConfirmTitle="Clear this assignment?"
										removeConfirmDescription="This role will no longer reference the selected contact. The person will remain in your contact list for reuse."
									/>
								)
							})()
						) : (
							<ContactAdd
								onSubmit={(draft) => onAssign(section.key, draft)}
								candidates={contactCandidates}
								directorySource={section.title}
								triggerLabel={section.addLabel}
							/>
						)}
					</Section>
					{index < sections.length - 1 ? <Divider /> : null}
				</Fragment>
			))}
		</>
	)
}
